<template>
	<div class="et-detail">
		<div class="et-head">
			<v-icon small class="mr-1" color="primary">{{ tipo.icona }}</v-icon>
			<span class="et-head-title">{{ tipo.label }}</span>
			<v-chip v-if="evento" x-small label color="primary" outlined class="ml-1">{{ evento }}</v-chip>
			<v-spacer></v-spacer>
			<v-btn x-small outlined color="primary" @click="$emit('edit')"><v-icon left x-small>mdi-pencil-outline</v-icon>Modifica</v-btn>
			<v-btn x-small text color="error" @click="$emit('delete')"><v-icon left x-small>mdi-delete-outline</v-icon>Elimina</v-btn>
		</div>

		<div class="et-body">
			<!-- anteprima dell'email nella lingua scelta -->
			<div class="et-block">
				<div class="et-section d-flex align-center">
					<span>Anteprima</span>
					<v-spacer></v-spacer>
					<div class="et-langs">
						<button
							v-for="t in traduzioni"
							:key="t.lang"
							type="button"
							class="et-lang"
							:class="{ 'et-lang--on': t.lang === lingua }"
							:title="'Anteprima in ' + t.lang.toUpperCase()"
							@click="lingua = t.lang"
						>
							<img :src="bandiera(t.lang)" :alt="t.lang" />
							<span>{{ t.lang.toUpperCase() }}</span>
						</button>
					</div>
				</div>
				<EmailPreview :traduzione="scelta" codificato />
			</div>

			<div class="et-side">
				<!-- stato delle traduzioni -->
				<div class="et-block">
					<div class="et-section">
						Lingue <span class="et-muted">· {{ complete }}/{{ righeLingue.length }} complete</span>
					</div>
					<div class="et-box">
						<div v-for="r in righeLingue" :key="r.lang" class="et-line">
							<img :src="bandiera(r.lang)" :alt="r.lang" class="et-flag" />
							<span class="et-line-lang">{{ r.lang.toUpperCase() }}</span>
							<span v-if="r.mancano.length" class="et-warn"><v-icon x-small color="warning">mdi-alert-outline</v-icon> manca {{ r.mancano.join(' e ') }}</span>
							<span v-else class="et-ok"><v-icon x-small color="success">mdi-check-circle-outline</v-icon> completa</span>
							<v-spacer></v-spacer>
							<span v-if="r.button" class="et-muted et-line-btn" :title="'Pulsante: ' + r.button">{{ r.button }}</span>
						</div>
					</div>
				</div>

				<!-- placeholder -->
				<div class="et-block">
					<div class="et-section">Placeholder usati</div>
					<div class="et-box et-ph">
						<span v-if="!placeholder.length" class="et-muted">Nessun placeholder: il testo è uguale per tutti</span>
						<v-chip
							v-for="p in placeholder"
							:key="p"
							x-small
							label
							outlined
							class="mr-1 mb-1"
							:color="PLACEHOLDER[p] ? 'primary' : 'error'"
							:title="PLACEHOLDER[p] || 'Placeholder non riconosciuto: nell\'email resta scritto così'"
						>
							[{{ p }}]
						</v-chip>
						<PlaceholderDifferenze :traduzioni="traduzioni" class="mb-1" />
					</div>
				</div>

				<div class="et-dates et-muted">
					<span v-if="item.created_at">Creato {{ item.created_at | moment('DD/MM/YYYY HH:mm') }}</span>
					<span v-if="item.updated_at"> · agg. {{ item.updated_at | moment('DD/MM/YYYY HH:mm') }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import he from 'he';
import EmailPreview from './EmailPreview.vue';
import PlaceholderDifferenze from './PlaceholderDifferenze.vue';
import { tipologia, PLACEHOLDER, testoPiano, placeholderUsati, bandiera } from './tipologie';

// dettaglio di un template email nella lista: anteprima per lingua, stato delle traduzioni e placeholder
export default {
	name: 'EmailTemplateDetail',
	components: { EmailPreview, PlaceholderDifferenze },
	props: {
		item: { type: Object, required: true },
		// lingue del profilo, la predefinita per prima
		lingue: { type: Array, default: () => [] },
	},
	data() {
		return {
			PLACEHOLDER,
			lingua: (this.item.trans && this.item.trans.lang) || this.lingue[0] || '',
		};
	},
	computed: {
		tipo() {
			return tipologia(this.item.type);
		},
		evento() {
			return this.item.status && this.item.status.value;
		},
		// una traduzione per ogni lingua del profilo (vuota se manca), più eventuali lingue non più attive
		traduzioni() {
			const salvate = this.item.traduzioni || (this.item.trans ? [this.item.trans] : []);
			const lingue = [...new Set([...this.lingue, ...salvate.map(t => t.lang)])].filter(Boolean);
			return lingue.map(lang => salvate.find(t => t.lang === lang) || { lang });
		},
		scelta() {
			return this.traduzioni.find(t => t.lang === this.lingua) || this.traduzioni[0] || {};
		},
		righeLingue() {
			return this.traduzioni.map(t => {
				const mancano = [];
				if (!String(t.oggetto || '').trim()) mancano.push('oggetto');
				if (!testoPiano(t.text)) mancano.push('testo');
				return { lang: t.lang, button: he.decode(String(t.button || '')), mancano };
			});
		},
		complete() {
			return this.righeLingue.filter(r => !r.mancano.length).length;
		},
		placeholder() {
			return [...new Set(this.traduzioni.flatMap(t => placeholderUsati(t)))];
		},
	},
	methods: {
		bandiera,
	},
};
</script>

<style scoped>
.et-detail {
	width: 100%;
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 10px;
	overflow: hidden;
	background: #f3f6fa;
	white-space: normal;
}
.et-head {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
	padding: 8px 12px;
	background: #fff;
	border-bottom: 1px solid #e1e7ef;
	font-size: 13px;
}
.et-head-title {
	font-weight: 600;
	color: #1f2d3d;
}
.et-body {
	display: grid;
	grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
	gap: 16px;
	padding: 12px;
}
.et-side {
	display: flex;
	flex-direction: column;
	gap: 16px;
	min-width: 0;
}
.et-section {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #5f6b7a;
	margin-bottom: 6px;
	min-height: 24px;
}
.et-muted {
	color: #5f6b7a;
	font-weight: 400;
	text-transform: none;
	letter-spacing: 0;
}
.et-box {
	background: #fff;
	border: 1px solid #e1e7ef;
	border-radius: 8px;
	overflow: hidden;
}
.et-langs {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
}
.et-lang {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 2px 6px;
	border: 1px solid #e1e7ef;
	border-radius: 6px;
	background: #fff;
	font-size: 11px;
	font-weight: 600;
	color: #5f6b7a;
	letter-spacing: 0;
}
.et-lang img {
	width: 16px;
	height: 12px;
	object-fit: cover;
}
.et-lang--on {
	border-color: #01579b;
	color: #01579b;
	background: #eef4fd;
}
.et-line {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 7px 10px;
	font-size: 13px;
}
.et-line + .et-line {
	border-top: 1px solid #eef2f7;
}
.et-flag {
	width: 18px;
	height: 13px;
	object-fit: cover;
	flex: 0 0 auto;
}
.et-line-lang {
	font-weight: 600;
	width: 22px;
	flex: 0 0 auto;
}
.et-ok {
	color: #2e7d32;
}
.et-warn {
	color: #a15c00;
}
.et-line-btn {
	font-size: 12px;
	max-width: 40%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.et-ph {
	padding: 8px 10px 4px;
	font-size: 13px;
}
.et-dates {
	font-size: 12px;
}

@media (max-width: 1023px) {
	.et-body {
		grid-template-columns: 1fr;
	}
}
@media (max-width: 768px) {
	.et-body {
		padding: 8px;
	}
	.et-line {
		flex-wrap: wrap;
	}
	.et-line-btn {
		max-width: 100%;
	}
}
</style>
